import { ChangeDetectionStrategy, Component, computed, inject, signal, type OnDestroy, type OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EditStateCoordinator } from '../entity-edit/edit-state-coordinator.service';
import { FIELD_PERMISSION_CHECKER, type FieldPermissionChecker } from '../entity-edit/field-permission-checker';
import { CustomPropertyDefinitionsService } from '../services/custom-property-definitions.service';
import { CustomPropertiesFormComponent } from './custom-properties-form.component';

/**
 * Thin wrapper around `CustomPropertiesFormComponent` for use with
 * `EntityEditSectionsHostComponent`.
 *
 * Reads `entityType` and `customProperties` from `EditStateCoordinator.context()`,
 * registers dirty state and save handler with the coordinator.
 *
 * Usage in section registration:
 * ```ts
 * { id: 'custom-properties', icon: 'tune', titleKey: 'common.customProperties.sectionTitle',
 *   priority: 100, component: CustomPropertiesSectionComponent, requiresEntityId: true }
 * ```
 */
@Component({
  selector: 'zag-custom-properties-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: contents' },
  imports: [CustomPropertiesFormComponent],
  template: `
    <zag-custom-properties-form
      [entityType]="entityType()"
      [values]="initialValues()"
      [disabled]="!canEdit()"
      (valuesChange)="onValuesChange($event)"
    />
  `,
})
export class CustomPropertiesSectionComponent implements OnInit, OnDestroy {
  private readonly coordinator = inject(EditStateCoordinator);
  private readonly definitionsApi = inject(CustomPropertyDefinitionsService);
  private readonly permissions = inject<FieldPermissionChecker>(FIELD_PERMISSION_CHECKER);

  private static readonly SECTION_ID = 'custom-properties';

  private readonly _values = signal<Record<string, unknown>>({});
  private readonly _isDirty = signal(false);

  readonly entityType = computed(() => {
    const ctx = this.coordinator.context();
    return (ctx['entityType'] as string) ?? '';
  });

  readonly initialValues = computed(() => {
    const ctx = this.coordinator.context();
    return (ctx['customProperties'] as Record<string, unknown>) ?? {};
  });

  readonly isEditMode = computed(() => this.coordinator.isEditMode());

  readonly canEdit = computed(() => {
    const permission = this.editPermission();
    return this.isEditMode() && (permission === null || this.permissions.hasPermission(permission));
  });

  private readonly editPermission = computed(() => {
    const permission = this.coordinator.context()['customPropertiesEditPermission'];
    return typeof permission === 'string' ? permission : null;
  });

  ngOnInit(): void {
    this.coordinator.registerSection(CustomPropertiesSectionComponent.SECTION_ID, {
      isDirty: this._isDirty,
      save: (entityId: string) => this.save(entityId),
    });
  }

  ngOnDestroy(): void {
    this.coordinator.unregisterSection(CustomPropertiesSectionComponent.SECTION_ID);
  }

  onValuesChange(values: Record<string, unknown>): void {
    this._values.set(values);
    const original = this.initialValues();
    this._isDirty.set(this.hasValuesChanged(values, original));
  }

  private hasValuesChanged(current: Record<string, unknown>, original: Record<string, unknown>): boolean {
    const allKeys = new Set([...Object.keys(current), ...Object.keys(original)]);
    return [...allKeys].some(k => !this.valuesEqual(current[k], original[k]));
  }

  private valuesEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((v, i) => v === b[i]);
    }
    return false;
  }

  private async save(entityId: string): Promise<void> {
    const entityType = this.entityType();
    if (!entityType) return;

    const ctx = this.coordinator.context();
    const resourcePath = ctx['resourcePath'] as string | undefined;
    if (!resourcePath) return;

    await firstValueFrom(this.definitionsApi.patchEntityCustomProperties(resourcePath, entityId, this._values()));
  }
}
